import { textoDaSaidaRecusada } from '@entregas/core';
import type { Db } from './cliente';
import { ErroApi, exigir, exigirOuNulo } from './erros';
import { paraPedido } from './mapeadores';
import { obterPedido, type PedidoNaLista } from './pedidos';

// O balcão na lista do dia (decisões B3 e B4): sair com um entregador, desfazer a saída, marcar entregue e
// pôr para sair de novo. Toda escrita é CONDICIONADA ao status que a tela viu: 0 linhas é recusa (o app do
// entregador andou antes, ou o RLS escondeu o pedido), nunca sucesso silencioso.

// Mesmas colunas de listarPedidos (pedidos.ts): a escrita já volta com os nomes que a lista mostra.
const COLUNAS_LISTA = '*, clientes(nome, telefone), entregadores(nome)';

type LinhaLista = Parameters<typeof paraPedido>[0] & {
  clientes: { nome: string; telefone: string } | null;
  entregadores: { nome: string } | null;
};

function paraPedidoNaLista(l: LinhaLista): PedidoNaLista {
  return {
    ...paraPedido(l),
    clienteNome: l.clientes?.nome ?? '',
    clienteTelefone: l.clientes?.telefone ?? '',
    entregadorNome: l.entregadores?.nome ?? null,
  };
}

const DOIS_DIAS_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * A lista do balcão: os criados no dia operacional, mais o que ainda pede ação de dias anteriores (para sair e
 * não entregue dos últimos 2 dias) e tudo a caminho, de qualquer dia (o que saiu antes da virada não some).
 * O mais antigo primeiro. Passando de 500, ficam os 500 mais NOVOS (o banco lê do mais novo e a lista vira).
 */
export async function listarPedidosDaFila(
  db: Db,
  janela: { inicio: string; fim: string },
): Promise<PedidoNaLista[]> {
  const desde = new Date(Date.parse(janela.inicio) - DOIS_DIAS_MS).toISOString();
  // Entre aspas: o instante ISO tem "." e ":", que o filtro `or` do PostgREST reserva.
  const doDia = `and(criado_em.gte."${janela.inicio}",criado_em.lt."${janela.fim}")`;
  const pendentes = `and(status.in.(pendente,nao_entregue),criado_em.gte."${desde}")`;
  const linhas = exigir(
    await db
      .from('pedidos')
      .select(COLUNAS_LISTA)
      .or(`${doDia},${pendentes},status.eq.a_caminho`)
      .order('criado_em', { ascending: false })
      .limit(500),
  );
  return linhas.map(paraPedidoNaLista).reverse();
}

/** O "Sai com" não gravou: o pedido não estava mais na loja. `atual` é como ele está agora (null = sumiu). */
export class SaidaRecusada extends ErroApi {
  readonly atual: PedidoNaLista | null;

  constructor(atual: PedidoNaLista | null) {
    super('pedido_ja_saiu', atual);
    this.name = 'SaidaRecusada';
    this.atual = atual;
    // "O Rafael já saiu com este pedido pelo app." em vez da mensagem genérica do código.
    this.message = textoDaSaidaRecusada(atual);
  }
}

/** Como o pedido estava antes do "Sai com": é o que o Desfazer devolve. */
export interface AntesDaSaida {
  entregadorId: string | null;
  /** O gatilho apaga o motivo ao sair; o pedido que voltou do Resolver precisa dele de volta. */
  motivo: string | null;
}

/**
 * "Sai com Carlos": uma escrita só, que vale apenas com o pedido ainda na loja. O gatilho carimba saiu_em e
 * trava a troca depois da saída (pedido_ja_saiu); se o app do entregador saiu antes, 0 linhas → recusa.
 */
export async function sairCom(
  db: Db,
  pedidoId: string,
  entregadorId: string,
): Promise<PedidoNaLista> {
  const linha = exigirOuNulo(
    await db
      .from('pedidos')
      .update({ entregador_id: entregadorId, status: 'a_caminho' })
      .eq('id', pedidoId)
      .eq('status', 'pendente')
      .select(COLUNAS_LISTA)
      .maybeSingle(),
  );
  if (linha) return paraPedidoNaLista(linha);
  throw new SaidaRecusada(await obterPedido(db, pedidoId));
}

/**
 * Desfaz o "Sai com" (5 s). 1ª escrita: volta para a loja com o motivo de antes, só se o pedido ainda está a
 * caminho com quem saiu pela loja (o Desfazer nunca desfaz a saída que o app fez). 0 linhas →
 * nao_da_para_desfazer. 2ª escrita, só se estava separado para OUTRO entregador: o gatilho barra trocar o
 * entregador na mesma escrita que sai de a_caminho (pedido_ja_saiu), então a troca vem depois, já pendente.
 * Se a 2ª falhar, devolve o pedido como ficou (na loja, separado para quem saiu): a tela compara com `antes`.
 */
export async function desfazerSaida(
  db: Db,
  pedidoId: string,
  quemSaiu: string,
  antes: AntesDaSaida,
): Promise<PedidoNaLista> {
  const linha = exigirOuNulo(
    await db
      .from('pedidos')
      .update({
        status: 'pendente',
        motivo: antes.motivo,
        ...(antes.entregadorId === null && { entregador_id: null }),
      })
      .eq('id', pedidoId)
      .eq('status', 'a_caminho')
      .eq('entregador_id', quemSaiu)
      .select(COLUNAS_LISTA)
      .maybeSingle(),
  );
  if (!linha) throw new ErroApi('nao_da_para_desfazer');
  const voltou = paraPedidoNaLista(linha);
  if (!antes.entregadorId || antes.entregadorId === quemSaiu) return voltou;

  try {
    const separado = exigirOuNulo(
      await db
        .from('pedidos')
        .update({ entregador_id: antes.entregadorId })
        .eq('id', pedidoId)
        .eq('status', 'pendente')
        .eq('entregador_id', quemSaiu)
        .select(COLUNAS_LISTA)
        .maybeSingle(),
    );
    return separado ? paraPedidoNaLista(separado) : voltou;
  } catch {
    return voltou;
  }
}

/** "Entregue" do entregador sem app (decisão B4), gravado depois dos 5 s. Só a caminho; entregue não volta. */
export async function marcarEntregue(db: Db, pedidoId: string): Promise<PedidoNaLista> {
  const linha = exigirOuNulo(
    await db
      .from('pedidos')
      .update({ status: 'entregue' })
      .eq('id', pedidoId)
      .eq('status', 'a_caminho')
      .select(COLUNAS_LISTA)
      .maybeSingle(),
  );
  if (!linha) throw new ErroApi('transicao_invalida');
  return paraPedidoNaLista(linha);
}

/**
 * "Voltar para a loja" do pedido a caminho, gravado só depois dos 5 s do Desfazer: o gatilho apaga o `saiu_em`
 * ao voltar e carimba um novo ao sair, então desfazer depois de gravar não devolveria o pedido igual. Vale só
 * se ainda está a caminho com quem a tela viu; 0 linhas (o app marcou entregue antes) → transicao_invalida.
 */
export async function voltarParaLoja(
  db: Db,
  pedidoId: string,
  entregadorId: string,
): Promise<PedidoNaLista> {
  const linha = exigirOuNulo(
    await db
      .from('pedidos')
      .update({ status: 'pendente' })
      .eq('id', pedidoId)
      .eq('status', 'a_caminho')
      .eq('entregador_id', entregadorId)
      .select(COLUNAS_LISTA)
      .maybeSingle(),
  );
  if (!linha) throw new ErroApi('transicao_invalida');
  return paraPedidoNaLista(linha);
}

/** Resolver → "Pôr para sair": o não entregue volta para a loja (com o motivo e o entregador de antes). */
export async function porParaSair(db: Db, pedidoId: string): Promise<PedidoNaLista> {
  const linha = exigirOuNulo(
    await db
      .from('pedidos')
      .update({ status: 'pendente' })
      .eq('id', pedidoId)
      .eq('status', 'nao_entregue')
      .select(COLUNAS_LISTA)
      .maybeSingle(),
  );
  if (!linha) throw new ErroApi('transicao_invalida');
  return paraPedidoNaLista(linha);
}
